import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, doc, setDoc } from "firebase/firestore";
import { db } from '../../../../firebase';
import OutletHead from '../../../../components/common/outlet-head';
import BookingForm from '../../../../components/bookings/forms';
import { IoArrowBackOutline } from 'react-icons/io5';
import { Button, Spinner } from 'react-bootstrap';
import { useAuth } from '../../../../context/AuthContext';
import { InvoiceNumber } from 'invoice-number';

function CreateBooking() {
  const { user, settings } = useAuth();
  const { bid } = user.claims;
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [bookingID, setBookingID] = useState(null);

  const [bookingData, setBookingData] = useState({
    physicalFileNumber: '',
    referenceID: '',
    bookingDate: null,
    partyDetails: null,
    projectDetails: null,
    discountPercent: 0,
    discountAmount: 0,
    basicAmount: 0,
    additionalCharges: 0,
    taxRate: 0,
    overdueInterestRate: 0,       // <-- NEW: per-booking overdue interest rate
    overdueTaxRate: 'none',       // <-- NEW: future-proof (can be 0 or "none")
    note: '',
    termsConditions: '',
    createdOn: new Date(),
    updatedOn: new Date()
  });

  const [selectedPayments, setSelectedPayments] = useState(null);
  const [inputError, setInputError] = useState(null);

  function generateBookingID() {
    let prefix = 'BK0';
    let currentYear = new Date().getFullYear();
    currentYear = currentYear.toString().substr(2, 4);
    let currentMonth = new Date().getMonth() + 1;
    currentMonth = currentMonth === 10 || currentMonth === 11 || currentMonth === 12 ? `${currentMonth}` : `0${currentMonth}`;
    let currentDate = new Date().getDate();
    let currentHours = new Date().getHours();
    currentHours = currentHours.toString().length === 2 ? `${currentHours}` : `0${currentHours}`;
    let currentMinutes = new Date().getMinutes();
    currentMinutes = currentMinutes.toString().length === 2 ? `${currentMinutes}` : `0${currentMinutes}`;
    let currentSeconds = new Date().getSeconds();
    currentSeconds = currentSeconds.toString().length === 2 ? `${currentSeconds}` : `0${currentSeconds}`;
    let bookID = `${prefix}${currentYear}${currentMonth}${currentDate}${currentHours}${currentMinutes}${currentSeconds}`;
    setBookingID(bookID);
  }

  useEffect(() => {
    const bookDate = new Date();

    setBookingData((prevData) => ({
      ...prevData,
      bookingDate: bookDate
    }));

    if (!bookingID) {
      generateBookingID();
    }

    if (settings) {
      settings.forEach((docItem) => {
        if (docItem.id === 'general') {
          const general = docItem;
          const taxRate = parseFloat(general.taxRate || 0);
          const overdueInterestRate = parseFloat(general.overdueInterest || 0);

          // Future-proof overdueTax:
          // - If not configured, we store "none"
          // - Else store numeric rate
          let overdueTaxRate = general.overdueTaxRate;
          if (
            overdueTaxRate === undefined ||
            overdueTaxRate === null ||
            overdueTaxRate === '' ||
            overdueTaxRate === 'none'
          ) {
            overdueTaxRate = 'none';
          } else {
            const parsed = parseFloat(overdueTaxRate);
            overdueTaxRate = Number.isNaN(parsed) ? 'none' : parsed;
          }

          setBookingData((prevData) => ({
            ...prevData,
            overdueInterestRate,
            overdueTaxRate,
            termsConditions: general.termsConditions || '',
            taxRate,
          }));
        }
      });
    }
  }, [settings, bookingID]);

  const handleSaveBookedUnit = async (
    propertyID,
    towerID,
    unitID,
    BookingID,
    unitFloor,
    unitNumber
  ) => {
    const tempBookedData = {
      propertyID,
      towerID,
      unitID,
      bookID: BookingID,
      unitFloor,
      unitNumber
    };

    try {
      await addDoc(
        collection(db, "Businesses", bid, "Properties", propertyID, "BookedUnits"),
        tempBookedData
      );
    } catch (error) {
      console.log('Booked Units Data [Error] : ', error);
    }
  };

  async function handleSavePayment(invoiceNumber, bookId, data) {
    try {
      await setDoc(
        doc(db, "Businesses", bid, "Bookings", bookId, "Invoices", invoiceNumber),
        data
      );
    } catch (error) {
      console.log('Creating Payments Data [Error] : ', error);
    }
  }

  const calculateDueDate = (bookingDate, completionDate, paymentTerms) => {
    const booking = new Date(bookingDate);
    const milestoneDate =
      completionDate && completionDate.seconds
        ? new Date(completionDate.seconds * 1000)
        : completionDate
          ? new Date(completionDate)
          : null;

    let baseDate;
    if (!milestoneDate || milestoneDate <= booking) {
      baseDate = booking;
    } else {
      baseDate = milestoneDate;
    }

    const dueDate = new Date(baseDate);
    dueDate.setDate(dueDate.getDate() + parseFloat(paymentTerms || 0));
    return dueDate;
  };

  const createPaymentPlans = (bookingID, data) => {
    let currentYear = new Date().getFullYear();
    let currentMonth = new Date().getMonth() + 1;
    let currentDay = new Date().getDate();

    currentMonth =
      currentMonth === 10 || currentMonth === 11 || currentMonth === 12
        ? `${currentMonth}`
        : `0${currentMonth}`;

    let invoiceNumber = 'INV-' + currentYear + currentMonth + currentDay + '000';

    // Apply discount on basic + additional
    const discountedBasic =
      parseFloat(data?.basicAmount || 0) -
      ((parseFloat(data?.discountPercent || 0) / 100) *
        parseFloat(data?.basicAmount || 0));

    const discountedCharges =
      parseFloat(data?.additionalCharges || 0) -
      ((parseFloat(data?.discountPercent || 0) / 100) *
        parseFloat(data?.additionalCharges || 0));

    if (selectedPayments && selectedPayments.length > 0) {
      for (let i = 0; i < selectedPayments.length; i++) {
        let principal = 0;
        let tax = 0;

        if (selectedPayments[i].valueType === 'percent') {
          const partBasic =
            (parseFloat(selectedPayments[i].value) / 100) *
            parseFloat(discountedBasic);
          const partCharges =
            (parseFloat(selectedPayments[i].other || 0) / 100) *
            parseFloat(discountedCharges);

          principal = partBasic + partCharges;
          tax = (principal * parseFloat(data.taxRate || 0)) / 100;
        }

        // Calculate due date
        let dueDate = null;
        if (selectedPayments[i].completionDate && selectedPayments[i].paymentTerms) {
          dueDate = calculateDueDate(
            data.bookingDate,
            selectedPayments[i].completionDate,
            selectedPayments[i].paymentTerms
          );
        }

        const invoiceDoc = {
          createdOn: new Date(),
          updatedOn: new Date(),
          planID: selectedPayments[i].id,
          name: selectedPayments[i].name,
          description: selectedPayments[i].description,
          completionDate: selectedPayments[i].completionDate || null,
          paymentTerms: selectedPayments[i].paymentTerms || null,
          dueDate: dueDate,
          isCompleted: selectedPayments[i].isCompleted,
          isPaid: false,

          // Unified Model
          principal: Math.round(principal * 100) / 100,
          tax: Math.round(tax * 100) / 100,

          principalReceived: 0,
          taxReceived: 0,

          overdueInterest: 0,
          overdueInterestReceived: 0,
          overdueTax: 0,
          overdueTaxReceived: 0,
          overdueDays: 0,

          // Store per-booking rates on invoice (for UI/backdate helper)
          overdueInterestRate: data.overdueInterestRate || 0,
          overdueTaxRate: data.overdueTaxRate ?? 'none'
        };

        invoiceNumber = InvoiceNumber.next(invoiceNumber);

        handleSavePayment(invoiceNumber, bookingID, invoiceDoc);
      }
    }
  };

  const handleSaveBookingData = async (id, data) => {
    try {
      await setDoc(doc(db, "Businesses", bid, "Bookings", id), data);

      if (
        data &&
        data.projectDetails &&
        data.projectDetails.propertyID &&
        data.projectDetails.propTower &&
        data.projectDetails.propTower.id &&
        data.projectDetails.bookedUnit &&
        data.projectDetails.bookedUnit.unitID
      ) {
        handleSaveBookedUnit(
          data.projectDetails.propertyID,
          data.projectDetails.propTower.id,
          data.projectDetails.bookedUnit.unitID,
          id,
          data.projectDetails.bookedUnit.unitFloor,
          data.projectDetails.bookedUnit.unitNumber
        );
      }

      const tempSubTotal =
        parseFloat(data.basicAmount || 0) +
        parseFloat(data.additionalCharges || 0);

      const tempTaxableAmount =
        parseFloat(tempSubTotal || 0) -
        parseFloat(data.discountAmount || 0);

      const tempTaxAmount =
        (parseFloat(tempTaxableAmount || 0) * parseFloat(data.taxRate || 0)) /
        100;

      const tempTotalAmount =
        parseFloat(tempTaxableAmount || 0) + parseFloat(tempTaxAmount || 0);

      // Create instalments
      createPaymentPlans(id, data);

      let narration = `New Booking (${id})`;
      let description = `${data.projectDetails.propName
        } (Floor - ${data.projectDetails.bookedUnit.unitFloor}, Unit Number - ${data.projectDetails.bookedUnit.unitFloor
        }0${data.projectDetails.bookedUnit.unitNumber})`;

      const ledgerData = {
        createdOn: new Date(),
        transactionDate: data && data.bookingDate,
        description: description,
        narration: narration,
        accountCode: 'Booking',
        debitAmount: null,
        creditAmount: tempTotalAmount,
        receiptID: null,
        invoiceID: null,
        partyID: data.partyDetails.id,
        bookingID: id,
        paymentDetails: null,
        taxRate: data && data.taxRate
      };

      await addDoc(collection(db, "Businesses", bid, "Ledger"), ledgerData);

      navigate(-1);
    } catch (error) {
      console.log('Creating Booking Data [Error] : ', error);
    }

    setIsLoading(false);
  };

  function handleBookingData(event) {
    event.preventDefault();
    const { partyDetails, projectDetails, bookingDate } = bookingData;

    if (!bookingID) {
      setInputError({ ...inputError, bookingID: 'This field is required.' });
    } else if (!partyDetails) {
      setInputError({ ...inputError, partyDetails: 'This field is required.' });
    } else if (!projectDetails) {
      setInputError({ ...inputError, projectDetails: 'This field is required.' });
    } else if (!bookingDate) {
      setInputError({ ...inputError, bookingDate: 'This field is required.' });
    } else {
      // setIsLoading(true);
      handleSaveBookingData(bookingID, bookingData);
    }
  }

  return (
    <section className='section'>
      <OutletHead
        left={
          <div className='d-flex align-items-center'>
            <Button
              variant='light'
              size='sm'
              className='p-2 me-3 rounded-circle'
              onClick={() => navigate(-1)}
            >
              <IoArrowBackOutline size={24} />
            </Button>
            <h5 className='mb-0'>Create New Booking</h5>
          </div>
        }
        right={
          <div className='d-flex align-items-center'>
            {bookingID &&
              bookingData.partyDetails &&
              bookingData.projectDetails &&
              bookingData.bookingDate ? (
              <Button
                disabled={isLoading && isLoading}
                variant="primary"
                type="submit"
                className='py-2 btn-width'
                onClick={handleBookingData}
              >
                {isLoading ? (
                  <span>
                    <Spinner size="sm" className='me-1' /> Saving
                  </span>
                ) : (
                  <span>Save Booking</span>
                )}
              </Button>
            ) : (
              <Button
                disabled
                variant="primary"
                type="submit"
                className='py-2 btn-width'
                onClick={handleBookingData}
              >
                <span>Save Booking</span>
              </Button>
            )}
          </div>
        }
      />
      <div className='border-top'>
        <div className='row'>
          <div className='col-lg-12'>
            <div className='mb-5 position-relative'>
              {isLoading ? (
                <div className='loader'>
                  <div className='spinner' />
                </div>
              ) : null}
              <BookingForm
                bookingID={bookingID}
                setBookingData={setBookingData}
                bookingData={bookingData}
                setSelectedPayments={setSelectedPayments}
                setInputError={setInputError}
                inputError={inputError}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default CreateBooking;